// Background location tracking helper.
// Combines: foreground 15-min interval + Periodic Background Sync (Chrome PWA only) +
// service-worker postMessage delegation. Falls back to foreground-only on unsupported browsers.
import api from "./api";

const STORAGE_KEY = "familyos_bg_location_enabled";
const PERIODIC_TAG = "familyos-location-sync";
const FOREGROUND_INTERVAL_MS = 15 * 60 * 1000; // 15 min

let foregroundTimer = null;
let swMessageHandler = null;

export function isBackgroundTrackingEnabled() {
  return localStorage.getItem(STORAGE_KEY) === "1";
}

async function takeFixAndUpload({ silent = true } = {}) {
  if (!navigator.geolocation) return { ok: false, reason: "no-geolocation" };
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/locations", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            address: "",
            battery: Math.round((await readBatteryLevel()) * 100),
          });
          resolve({ ok: true });
        } catch (e) {
          if (!silent) console.warn("location upload failed", e);
          resolve({ ok: false, reason: "upload-failed" });
        }
      },
      (err) => {
        if (!silent) console.warn("geolocation error", err);
        resolve({ ok: false, reason: err.code === 1 ? "denied" : "unavailable" });
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
    );
  });
}

async function readBatteryLevel() {
  try {
    if ("getBattery" in navigator) {
      const b = await navigator.getBattery();
      return b.level || 1;
    }
  } catch {}
  return 1;
}

async function tryRegisterPeriodicSync() {
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no-sw" };
  const reg = await navigator.serviceWorker.ready;
  if (!("periodicSync" in reg)) return { ok: false, reason: "no-periodic-sync" };
  try {
    const status = await navigator.permissions.query({ name: "periodic-background-sync" });
    if (status.state !== "granted") return { ok: false, reason: "permission-not-granted" };
    await reg.periodicSync.register(PERIODIC_TAG, { minInterval: FOREGROUND_INTERVAL_MS });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "register-failed", error: String(e) };
  }
}

async function unregisterPeriodicSync() {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    if ("periodicSync" in reg) {
      const tags = await reg.periodicSync.getTags();
      if (tags.includes(PERIODIC_TAG)) await reg.periodicSync.unregister(PERIODIC_TAG);
    }
  } catch {}
}

function attachSwMessageListener() {
  if (swMessageHandler || !("serviceWorker" in navigator)) return;
  swMessageHandler = (event) => {
    if (event.data?.type === "FAMILYOS_TAKE_LOCATION_FIX") {
      takeFixAndUpload({ silent: true });
    }
  };
  navigator.serviceWorker.addEventListener("message", swMessageHandler);
}

function detachSwMessageListener() {
  if (!swMessageHandler || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.removeEventListener("message", swMessageHandler);
  swMessageHandler = null;
}

function startForegroundLoop() {
  stopForegroundLoop();
  foregroundTimer = setInterval(() => {
    if (document.visibilityState === "visible" || document.hasFocus()) {
      takeFixAndUpload({ silent: true });
    }
  }, FOREGROUND_INTERVAL_MS);
}

function stopForegroundLoop() {
  if (foregroundTimer) {
    clearInterval(foregroundTimer);
    foregroundTimer = null;
  }
}

/** Enable background tracking. Asks for location permission, takes one immediate fix,
 *  registers Periodic Background Sync (best-effort), starts a foreground 15-min loop,
 *  and listens for service-worker prompts. */
export async function enableBackgroundTracking() {
  if (!navigator.geolocation) return { ok: false, reason: "no-geolocation" };
  // Trigger geolocation prompt with an immediate fix
  const first = await takeFixAndUpload({ silent: false });
  if (!first.ok && first.reason === "denied") {
    return { ok: false, reason: "permission-denied" };
  }
  localStorage.setItem(STORAGE_KEY, "1");
  startForegroundLoop();
  attachSwMessageListener();
  const periodic = await tryRegisterPeriodicSync();
  return {
    ok: true,
    immediate_fix: first.ok,
    periodic_sync: periodic.ok,
    periodic_reason: periodic.reason || null,
  };
}

/** Disable background tracking. */
export async function disableBackgroundTracking() {
  localStorage.removeItem(STORAGE_KEY);
  stopForegroundLoop();
  detachSwMessageListener();
  await unregisterPeriodicSync();
  return { ok: true };
}

/** Re-arm background tracking on app boot if it was previously enabled. */
export function bootstrapBackgroundTracking() {
  if (!isBackgroundTrackingEnabled()) return;
  startForegroundLoop();
  attachSwMessageListener();
  // We don't re-prompt for geolocation here — user already granted it.
  // Take an immediate silent fix on boot.
  takeFixAndUpload({ silent: true });
}
